import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
// Using require() instead of ES6 import to prevent SSR errors - Datadog RUM SDK is browser-only
const datadogRum = require('@datadog/browser-rum').datadogRum;
import config from './../../config';
import APIClient from './../../helpers/ApiClient';
import logger from '../../logger/logger';

const initialState: any = {
  id: '',
  name: '',
  publicationDate: '',
  lastModifiedTimestamp: '',
  groups: [],
  defaultTemplateId: '',
  loading: false,
  loaded: false,
  metaCollection: null,
  metaCollectionName: '',
  metaCollectionId: '',
  type: '',
  selectedTemplateId: '',
  selectedTemplateName: '',
  params: {
    collectionId: '',
    templateId: '',
  },
};

interface LoadCollectionInterface {
  collectionId: string;
  templateId?: string;
}

export const loadCollection = createAsyncThunk(
  'collection/loadCollection',
  async (loadCollectionInterface: LoadCollectionInterface) => {
    const url = `${config.apiHost}/collections/${loadCollectionInterface.collectionId}`;
    try {
      const res = await APIClient('get', url);
      return await res.data;
    } catch (error: any) {
      const errorContext = {
        source: 'collectionSlice',
        collectionId: loadCollectionInterface.collectionId,
        templateId: loadCollectionInterface.templateId,
        url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        errorCode: error.code,
      };

      // Use datadogRum in browser, pino logger on server (SSR)
      if (globalThis.window !== undefined && datadogRum && datadogRum.addError !== undefined) {
        datadogRum.addError(error, errorContext);
      } else {
        logger.error({ err: error, ...errorContext }, 'Failed to load collection');
      }
      throw error;
    }
  },
);

export const collectionSlice = createSlice({
  name: 'collection',
  initialState,
  reducers: {
    setSelectedTemplate(state, action: PayloadAction<string>) {
      state.selectedTemplateId = action.payload;
      state.selectedTemplateName = state.groups
        .reduce((arr: { Id: string; Name: string }[], curr) => arr.concat(curr.Templates), [])
        .find((x) => x.Id == action.payload)?.Name;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action: any) => {
      return {
        ...state,
        ...action.payload.collection,
      };
    });
    builder.addCase(loadCollection.pending, (state, action) => {
      state.id = action.payload;
      state.metaCollection = null;
      state.metaCollectionName = null;
      state.metaCollectionId = null;
      state.loading = true;
      state.loaded = false;
    });
    builder.addCase(loadCollection.fulfilled, (state, action) => {
      state.id = action.payload.Id;
      state.defaultTemplateId = action.payload.DefaultTemplateId;
      state.name = action.payload.Name;
      state.publicationDate = action.payload.PublicationDate;
      state.lastModifiedTimestamp = action.payload.LastModifiedTimestamp;
      state.groups = action.payload.Groups;
      state.type = action.payload.Type;
      state.metaCollectionId = action.payload.DefaultMetaCollectionProjection
        ? action.payload.DefaultMetaCollectionProjection.Id
        : null;
      state.metaCollection = action.payload.DefaultMetaCollectionProjection
        ? action.payload.DefaultMetaCollectionProjection.Collections
        : null;
      state.metaCollectionName = action.payload.DefaultMetaCollectionProjection
        ? action.payload.DefaultMetaCollectionProjection.Name
        : null;
      state.selectedTemplateId = action.meta.arg.templateId
        ? action.meta.arg.templateId
        : action.payload.Groups[0].Templates[0].Id;
      state.selectedTemplateName = action.payload.Groups.reduce(
        (arr: { Id: string; Name: string }[], curr) => arr.concat(curr.Templates),
        [],
      ).find((x) => x.Id == state.selectedTemplateId)?.Name;
      state.params.collectionId = action.meta.arg.collectionId;
      state.params.templateId = action.meta.arg.templateId;
      state.loading = false;
      state.loaded = true;
    });
    builder.addCase(loadCollection.rejected, (state, action) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.error.message;
    });
  },
});

export const selectCollection = (state) => state.collection;

// Action creators are generated for each case reducer function
export const { setSelectedTemplate } = collectionSlice.actions;

export default collectionSlice.reducer;
